/**
 * Animates a <details> open and closed.
 *
 * <details> cannot be transitioned natively: the content is removed from the
 * box tree the instant `open` is dropped, so a CSS transition has nothing left
 * to animate on the way out. This holds the element open until the closing
 * animation finishes, then closes it for real.
 *
 * Rows inside a [data-accordion-group] container are exclusive: opening one
 * closes whichever other row in the group is open.
 *
 * Falls back to native behaviour when the visitor prefers reduced motion, and
 * if this script fails to load the accordion still opens and closes.
 */
// Several sections on one page load this script. Defining a custom element
// twice throws, and so does redeclaring a top-level class, so both are guarded.
if (!customElements.get('faq-accordion')) {
  class FaqAccordion extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.accordion__content');

      if (!this.details || !this.summary || !this.content) return;

      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;

      this.summary.addEventListener('click', this.onClick.bind(this));
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get animationOptions() {
      return { duration: 250, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' };
    }

    onClick(event) {
      const opening = this.isClosing || !this.details.open;
      if (opening) this.closeOthersInGroup();

      if (this.prefersReducedMotion) return;

      event.preventDefault();
      this.style.overflow = 'hidden';

      if (opening) {
        this.expand();
      } else {
        this.shrink();
      }
    }

    closeOthersInGroup() {
      const group = this.closest('[data-accordion-group]');
      if (!group) return;

      group.querySelectorAll('faq-accordion').forEach((row) => {
        if (row !== this && row.details) row.close();
      });
    }

    close() {
      if (!this.details.open || this.isClosing) return;

      if (this.prefersReducedMotion) {
        this.details.open = false;
        return;
      }

      this.style.overflow = 'hidden';
      this.shrink();
    }

    expand() {
      // Read the height before stopping a running close, so a reversal starts
      // from where the row visibly is rather than jumping.
      const startHeight = `${this.offsetHeight}px`;
      this.stop();
      this.isClosing = false;
      this.style.height = startHeight;
      this.details.open = true;

      window.requestAnimationFrame(() => {
        this.isExpanding = true;

        // Measuring at auto accounts for the content's own margins and the
        // element's borders, which a summary + content sum would miss.
        this.style.height = 'auto';
        const endHeight = `${this.offsetHeight}px`;
        this.style.height = startHeight;

        this.animate({ height: [startHeight, endHeight] }, this.animationOptions);
        this.run(true);
      });
    }

    shrink() {
      this.isClosing = true;

      // A running animation overrides layout, so it has to be stopped before
      // the closed height can be measured.
      const startHeight = `${this.offsetHeight}px`;
      this.stop();
      this.style.height = '';
      this.details.open = false;
      const endHeight = `${this.offsetHeight}px`;
      this.details.open = true;

      this.animate({ height: [startHeight, endHeight] }, this.animationOptions);
      this.run(false);
    }

    animate(keyframes, options) {
      this.stop();
      this.animation = super.animate(keyframes, options);
    }

    // Handlers are detached first: the cancel event fires asynchronously and
    // would otherwise clear the flags of the animation that replaced it.
    stop() {
      if (!this.animation) return;

      this.animation.onfinish = null;
      this.animation.oncancel = null;
      this.animation.cancel();
      this.animation = null;
    }

    run(open) {
      this.animation.onfinish = () => this.reset(open);
      this.animation.oncancel = () => {
        this.isClosing = false;
        this.isExpanding = false;
      };
    }

    reset(open) {
      this.details.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.style.height = '';
      this.style.overflow = '';
    }
  }

  customElements.define('faq-accordion', FaqAccordion);
}
